<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import Button from 'primevue/button';
  import OverlayBadge from 'primevue/overlaybadge';
  import Popover from 'primevue/popover';
  import { Bell, BellDot } from 'lucide-vue-next';
  import { useMarkNoticeReadMutation, useMyNoticesQuery } from '../api';
  import type { Notice } from '../types';
  import '../locales';

  const { t } = useI18n();
  const router = useRouter();

  const panel = ref<InstanceType<typeof Popover> | null>(null);

  const query = useMyNoticesQuery(() => ({ page: 1, page_size: 10 }));
  const markReadMutation = useMarkNoticeReadMutation();

  const notices = computed<Notice[]>(() => query.data.value?.items ?? []);
  const unreadCount = computed(() => notices.value.filter((n) => !n.is_read).length);
  const hasUnread = computed(() => unreadCount.value > 0);

  function formatTimestamp(iso: string): string {
    try {
      return new Date(iso).toLocaleString('de-AT');
    } catch {
      return iso;
    }
  }

  function togglePanel(event: Event): void {
    panel.value?.toggle(event);
  }

  async function onClickNotice(notice: Notice): Promise<void> {
    if (!notice.is_read) {
      try {
        await markReadMutation.mutateAsync(notice.notice_id);
      } catch {
        // Ignore — query will refetch on next interval.
      }
    }
    if (notice.link_path) {
      panel.value?.hide();
      void router.push(notice.link_path);
    }
  }

  async function onMarkAllRead(): Promise<void> {
    const unread = notices.value.filter((n) => !n.is_read);
    await Promise.all(
      unread.map((n) =>
        markReadMutation.mutateAsync(n.notice_id).catch(() => {
          /* swallow */
        }),
      ),
    );
  }

  function onShowAll(): void {
    panel.value?.hide();
    void router.push('/notifications');
  }
</script>

<template>
  <div class="inline-flex items-center">
    <OverlayBadge
      v-if="hasUnread"
      :value="unreadCount > 99 ? '99+' : String(unreadCount)"
      severity="danger"
      data-testid="notification-bell-badge"
    >
      <Button
        type="button"
        severity="secondary"
        text
        rounded
        :aria-label="t('notifications.bell.open')"
        data-testid="notification-bell-button"
        @click="togglePanel"
      >
        <BellDot :size="20" />
      </Button>
    </OverlayBadge>
    <Button
      v-else
      type="button"
      severity="secondary"
      text
      rounded
      :aria-label="t('notifications.bell.open')"
      data-testid="notification-bell-button"
      @click="togglePanel"
    >
      <Bell :size="20" />
    </Button>

    <Popover
      ref="panel"
      data-testid="notification-bell-panel"
      append-to="body"
      :show-close-icon="false"
      class="w-80"
    >
      <div class="flex flex-col gap-2 w-80">
        <div class="flex items-center justify-between">
          <span class="text-sm font-semibold text-surface-800">
            {{ t('notifications.bell.label') }}
          </span>
          <Button
            v-if="hasUnread"
            size="small"
            severity="secondary"
            text
            :label="t('notifications.bell.mark_all_read')"
            data-testid="notification-bell-mark-all-read"
            @click="onMarkAllRead"
          />
        </div>

        <div
          v-if="query.isLoading.value"
          class="py-4 text-center text-sm text-surface-500"
          data-testid="notification-bell-loading"
        >
          {{ t('notifications.bell.loading') }}
        </div>

        <ul
          v-else-if="notices.length > 0"
          class="flex max-h-80 flex-col gap-1 overflow-y-auto"
          data-testid="notification-bell-list"
        >
          <li
            v-for="notice in notices"
            :key="notice.notice_id"
            :data-testid="`notification-bell-item-${notice.notice_id}`"
            :class="[
              'cursor-pointer rounded border border-surface-100 p-2 text-sm hover:bg-surface-50',
              notice.is_read
                ? 'bg-white text-surface-600'
                : 'bg-primary-50 font-medium text-surface-800',
            ]"
            @click="onClickNotice(notice)"
          >
            <div class="flex items-start justify-between gap-2">
              <span class="font-semibold">{{ notice.title }}</span>
              <span class="shrink-0 text-xs text-surface-500">
                {{ formatTimestamp(notice.created_at) }}
              </span>
            </div>
            <p class="mt-1 line-clamp-2 text-xs text-surface-600">
              {{ notice.body }}
            </p>
          </li>
        </ul>

        <div
          v-else
          class="py-4 text-center text-sm text-surface-500"
          data-testid="notification-bell-empty"
        >
          {{ t('notifications.bell.empty') }}
        </div>

        <div class="mt-2 border-t border-surface-100 pt-2">
          <Button
            text
            severity="secondary"
            class="w-full"
            :label="t('notifications.bell.show_all')"
            data-testid="notification-bell-show-all"
            @click="onShowAll"
          />
        </div>
      </div>
    </Popover>
  </div>
</template>
